import { useState, useEffect, useCallback } from 'react'
import subscriptionService, { Plan, Subscription } from '@/services/subscription'
import { useAuthStore } from '@/stores/authStore'

/**
 * Hook personnalisé pour gérer les abonnements
 * Fournit un état et des méthodes pour interagir avec les abonnements
 */
export const useSubscription = () => {
  const { isAuthenticated } = useAuthStore()
  const [plans, setPlans] = useState<Plan[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [activeSubscription, setActiveSubscription] = useState<Subscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /**
   * Charge les plans disponibles
   */
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await subscriptionService.getPlans()
      setPlans(data)
    } catch (err) {
      setError('Erreur lors du chargement des plans')
      console.error('Error loading plans:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Charge les abonnements de l'utilisateur
   */
  const loadSubscriptions = useCallback(async () => {
    try {
      setError(null)
      const response = await subscriptionService.getMySubscriptions()
      setSubscriptions(response.subscriptions)
      
      // Trouver l'abonnement actif ou en période d'essai
      const primarySubscription =
        response.subscriptions.find(sub => sub.status === 'active') ??
        response.subscriptions.find(sub => sub.status === 'trialing') ??
        null

      setActiveSubscription(primarySubscription)
    } catch (err) {
      setError('Erreur lors du chargement des abonnements')
      console.error('Error loading subscriptions:', err)
    }
  }, [])

  /**
   * S'abonner à un plan
   */
  const subscribe = useCallback(async (planId: string) => {
    try {
      setError(null)
      await subscriptionService.redirectToCheckout(planId)
    } catch (err) {
      setError('Erreur lors de la création de la session de paiement')
      console.error('Error subscribing:', err)
      throw err
    }
  }, [])

  /**
   * Annuler un abonnement
   */
  const cancelSubscription = useCallback(async (subscriptionId: number) => {
    try {
      setError(null)
      await subscriptionService.cancelSubscription(subscriptionId)
      // Recharger les abonnements après annulation
      await loadSubscriptions()
      return true
    } catch (err) {
      setError('Erreur lors de l\'annulation de l\'abonnement')
      console.error('Error canceling subscription:', err)
      return false
    }
  }, [loadSubscriptions])

  /**
   * Vérifie si l'utilisateur a un plan actif
   */
  const hasActivePlan = useCallback((planName: string): boolean => {
    if (!activeSubscription) {
      return false
    }

    if (activeSubscription.plan === planName) {
      return true
    }

    // Considérer le plan d'essai comme équivalent au plan premium
    if (
      planName.toUpperCase() === 'PREMIUM' &&
      activeSubscription.plan?.toUpperCase() === 'PREMIUM_TRIAL'
    ) {
      return true
    }

    return false
  }, [activeSubscription])

  /**
   * Vérifie si l'utilisateur a un abonnement actif (quel que soit le plan)
   */
  const hasActiveSubscription = useCallback((): boolean => {
    if (!activeSubscription) {
      return false
    }

    const normalizedStatus = activeSubscription.status?.toLowerCase()
    if (normalizedStatus === 'active') {
      return true
    }

    return activeSubscription.plan?.toUpperCase() === 'PREMIUM_TRIAL'
  }, [activeSubscription])

  /**
   * Récupère le niveau d'accès actif de l'utilisateur
   */
  const getActiveAccessLevel = useCallback((): 'basic' | 'premium' | 'vip' | null => {
    return activeSubscription?.accessLevel || null
  }, [activeSubscription])

  /**
   * Vérifie si l'utilisateur a accès au niveau donné ou supérieur
   */
  const hasAccessLevel = useCallback((requiredLevel: 'basic' | 'premium' | 'vip'): boolean => {
    const accessLevel = activeSubscription?.accessLevel

    if (!accessLevel) return false
    
    const levels = { basic: 1, premium: 2, vip: 3 }
    const userLevel = levels[accessLevel] || 0
    const requiredLevelValue = levels[requiredLevel] || 0
    
    return userLevel >= requiredLevelValue
  }, [activeSubscription, subscriptions])

  /**
   * Réinitialise l'état des abonnements
   */
  const resetSubscriptions = useCallback(() => {
    setSubscriptions([])
    setActiveSubscription(null)
    setError(null)
  }, [])

  /**
   * Charge les plans au montage
   */
  useEffect(() => {
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Charger les plans une seule fois

  /**
   * Charge ou réinitialise les abonnements selon l'état d'authentification
   */
  useEffect(() => {
    if (isAuthenticated) {
      loadSubscriptions()
    } else {
      // Réinitialiser les abonnements si l'utilisateur n'est pas authentifié
      resetSubscriptions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]) // Seulement réagir aux changements d'authentification

  return {
    // État
    plans,
    subscriptions,
    activeSubscription,
    loading,
    error,
    
    // Méthodes
    subscribe,
    cancelSubscription,
    loadPlans,
    loadSubscriptions,
    resetSubscriptions,
    hasActivePlan,
    hasActiveSubscription,
    getActiveAccessLevel,
    hasAccessLevel,
  }
}

export default useSubscription


